import { useState, useEffect } from "react";

interface CalendarData {
  month: string;
  days: Record<string, number>;
}

const DAY_HEADERS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function todayStr() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function shiftMonth(monthKey: string, delta: number) {
  const parts = monthKey.split("-").map(Number);
  let y = parts[0] ?? 0;
  let m = (parts[1] ?? 1) + delta;
  while (m < 1) { m += 12; y--; }
  while (m > 12) { m -= 12; y++; }
  return `${y}-${String(m).padStart(2, "0")}`;
}

function formatMonthLabel(monthKey: string) {
  const parts = monthKey.split("-").map(Number);
  const y = parts[0] ?? 0;
  const m = parts[1] ?? 1;
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function colorFor(total: number | undefined, isPast: boolean): string {
  if (!isPast) return "bg-[#0f3460] text-gray-400";
  if (total === undefined || total === 0) return "bg-green-700/70 text-white";
  if (total <= 2) return "bg-yellow-600/80 text-white";
  return "bg-red-600/80 text-white";
}

export function CalendarPage() {
  const today = todayStr();
  const [monthKey, setMonthKey] = useState(today.slice(0, 7));
  const [data, setData] = useState<CalendarData | null>(null);

  useEffect(() => {
    setData(null);
    fetch(`/api/calendar?month=${monthKey}`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData({ month: monthKey, days: {} }));
  }, [monthKey]);

  const parts = monthKey.split("-").map(Number);
  const year = parts[0] ?? 0;
  const month = parts[1] ?? 1;
  const firstDay = new Date(year, month - 1, 1);
  const daysInMonth = new Date(year, month, 0).getDate();
  const leadingBlanks = (firstDay.getDay() + 6) % 7; // 0 = Mon

  const cells: ({ date: string; day: number; total: number | undefined; isPast: boolean; isToday: boolean } | null)[] = [];
  for (let i = 0; i < leadingBlanks; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const date = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({
      date,
      day: d,
      total: data?.days[date],
      isPast: date < today,
      isToday: date === today,
    });
  }
  while (cells.length % 7 !== 0) cells.push(null);

  let monthTotal = 0;
  let drinkingDays = 0;
  let dryDays = 0;
  if (data) {
    for (let d = 1; d <= daysInMonth; d++) {
      const date = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      if (date >= today) continue;
      const t = data.days[date] || 0;
      monthTotal += t;
      if (t > 0) drinkingDays++;
      else dryDays++;
    }
  }

  return (
    <div className="max-w-lg mx-auto p-4">
      <div className="bg-[#16213e] rounded-xl p-4 mb-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setMonthKey((m) => shiftMonth(m, -1))}
            className="text-gray-400 hover:text-white px-2 py-1 text-lg cursor-pointer"
          >
            ‹
          </button>
          <span className="font-medium">{formatMonthLabel(monthKey)}</span>
          <button
            onClick={() => setMonthKey((m) => shiftMonth(m, 1))}
            className="text-gray-400 hover:text-white px-2 py-1 text-lg cursor-pointer"
          >
            ›
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-1">
          {DAY_HEADERS.map((d) => (
            <div key={d} className="text-center text-[10px] uppercase tracking-wide text-gray-500 py-1">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {cells.map((c, i) => {
            if (!c) return <div key={`b-${i}`} className="aspect-square" />;
            const color = colorFor(c.total, c.isPast);
            return (
              <div
                key={c.date}
                className={`aspect-square rounded-md flex flex-col items-center justify-center text-xs ${color} ${c.isToday ? "ring-2 ring-blue-400" : ""}`}
              >
                <span className="font-semibold leading-none">{c.day}</span>
                {c.isPast && c.total !== undefined && c.total > 0 && (
                  <span className="text-[10px] opacity-90 leading-tight mt-0.5">
                    {c.total.toFixed(1)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-[#16213e] rounded-xl p-4 mb-4">
        <h3 className="text-xs uppercase tracking-wide text-gray-400 mb-3">Legend</h3>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-3">
            <span className="w-5 h-5 rounded bg-green-700/70 inline-block" />
            <span>No drinks</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="w-5 h-5 rounded bg-yellow-600/80 inline-block" />
            <span>2 units or fewer</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="w-5 h-5 rounded bg-red-600/80 inline-block" />
            <span>More than 2 units</span>
          </div>
        </div>
      </div>

      {data && (
        <div className="bg-[#16213e] rounded-xl p-4 grid grid-cols-3 gap-2 text-center">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-400">Total</div>
            <div className="font-bold text-lg">{monthTotal.toFixed(1)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-400">Dry days</div>
            <div className="font-bold text-lg text-green-400">{dryDays}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-400">Drinking</div>
            <div className="font-bold text-lg">{drinkingDays}</div>
          </div>
        </div>
      )}
    </div>
  );
}
